import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, type MouseEvent } from "react"
import { Extension, type Editor, type JSONContent, type TextSerializer } from "@tiptap/core"
import Document from "@tiptap/extension-document"
import Paragraph from "@tiptap/extension-paragraph"
import Text from "@tiptap/extension-text"
import { Placeholder } from "@tiptap/extensions"
import { EditorContent, useEditor } from "@tiptap/react"
import { splitBlock } from "@tiptap/pm/commands"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"
import { isSkillSlashSuggestionActive, SkillSlashAutocomplete } from "@/components/agent/skill-slash-suggestion"
import { hasSkillSlashToken, SKILL_SLASH_TOKEN_RE } from "@/components/agent/skill-slash-token"
import { insertDictatedText } from "@/components/prompt-editor/dictation-insert"
import { PromptMention } from "@/components/prompt-editor/mention-node"
import { createMentionSuggestion, isMentionSuggestionActive } from "@/components/prompt-editor/suggestion"
import { mentionToken, type MentionCategory, type MentionItem } from "@/components/prompt-editor/types"
import type { AgentSessionSkill } from "@/lib/agent-api"
import { agentComposerTextToDoc } from "@/lib/agent-image-mentions"
import { flowNodeMentionIdsFromText } from "@/lib/flow-node-mentions"
import { cn } from "@/lib/utils"

export { SKILL_SLASH_TOKEN_RE } from "@/components/agent/skill-slash-token"

const skillSlashHighlightKey = new PluginKey("agentSkillSlashHighlight")
/** Стабильный дефолт: новый `[]` на каждый рендер перезапускал бы пересчёт подписей нод. */
const NO_MENTIONS: MentionItem[] = []

const SkillSlashHighlight = Extension.create({
  name: "agentSkillSlashHighlight",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: skillSlashHighlightKey,
        props: {
          decorations(state) {
            const decorations: ReturnType<typeof Decoration.inline>[] = []
            state.doc.descendants((node, pos) => {
              if (!node.isText || !node.text) return
              const text = node.text
              SKILL_SLASH_TOKEN_RE.lastIndex = 0
              let match: RegExpExecArray | null
              while ((match = SKILL_SLASH_TOKEN_RE.exec(text)) != null) {
                const from = pos + match.index
                const to = from + match[0].length
                decorations.push(
                  Decoration.inline(from, to, {
                    class: "font-mono text-ui-link",
                  }),
                )
              }
            })
            return DecorationSet.create(state.doc, decorations)
          },
        },
      }),
    ]
  },
})

export type AgentComposerEditorHandle = {
  focus: () => void
  getText: () => string
  clear: () => void
  /** Insert `/${slug} ` (trailing space). No-op if token already present. */
  insertSkill: (slug: string) => void
  /** Insert plain text at the caret, spacing it off the preceding word. Used by voice dictation. */
  insertText: (text: string) => void
}

type Props = {
  value: string
  onChange: (text: string) => void
  onSubmit: () => void
  onFocus?: () => void
  placeholder?: string
  disabled?: boolean
  expanded?: boolean
  /** Extra top padding when a floating tab pill sits on the composer top edge. */
  edgeClearance?: boolean
  className?: string
  skills?: AgentSessionSkill[]
  onSkillPicked?: (skill: AgentSessionSkill) => void
  /** Board nodes offered by `@`. */
  nodeMentionItems?: MentionItem[]
  /** Attached chat images, `@img_1` first. Shown ahead of nodes. */
  imageMentionItems?: MentionItem[]
}

const mentionTextSerializer: TextSerializer = ({ node }) => {
  const category = String(node.attrs.category ?? "reference") as MentionCategory
  const stateRaw = node.attrs.stateId
  const stateId = stateRaw == null || stateRaw === "" ? null : Number(stateRaw)
  const attrRaw = node.attrs.attrKey
  const attrKey = attrRaw == null || attrRaw === "" ? null : String(attrRaw)
  return mentionToken(category, Number(node.attrs.id ?? 0), String(node.attrs.label ?? ""), stateId, attrKey)
}

function editorPlainText(editor: Editor | null): string {
  if (!editor) return ""
  return editor.getText({ blockSeparator: "\n", textSerializers: { mention: mentionTextSerializer } })
}

function composerDoc(text: string, items: readonly MentionItem[]): JSONContent {
  return agentComposerTextToDoc(text, items)
}

export const AgentComposerEditor = forwardRef<AgentComposerEditorHandle, Props>(function AgentComposerEditor(
  {
    value,
    onChange,
    onSubmit,
    onFocus,
    placeholder,
    disabled = false,
    expanded = false,
    edgeClearance = false,
    className,
    skills = [],
    onSkillPicked,
    nodeMentionItems = NO_MENTIONS,
    imageMentionItems = NO_MENTIONS,
  },
  ref,
) {
  const onChangeRef = useRef(onChange)
  const onSubmitRef = useRef(onSubmit)
  const onFocusRef = useRef(onFocus)
  const skillsRef = useRef(skills)
  const onSkillPickedRef = useRef(onSkillPicked)
  const nodeMentionItemsRef = useRef(nodeMentionItems)
  const mentionItemsRef = useRef<MentionItem[]>([])
  const lastEmittedRef = useRef(value)
  onChangeRef.current = onChange
  onSubmitRef.current = onSubmit
  onFocusRef.current = onFocus
  skillsRef.current = skills
  onSkillPickedRef.current = onSkillPicked
  nodeMentionItemsRef.current = nodeMentionItems
  mentionItemsRef.current = [...imageMentionItems, ...nodeMentionItems]

  const extensions = useMemo(
    () => [
      Document,
      Paragraph,
      Text,
      Placeholder.configure({ placeholder: placeholder ?? "" }),
      SkillSlashHighlight,
      SkillSlashAutocomplete.configure({
        getSkills: () => skillsRef.current,
        getOnSkillPicked: () => onSkillPickedRef.current,
      }),
      PromptMention.configure({
        getMentionItems: () => mentionItemsRef.current,
        suggestion: createMentionSuggestion(
          () => mentionItemsRef.current,
          () => "above",
          () => undefined,
          {
            categoryOrder: ["img", "vid", "node"],
            allow: () => mentionItemsRef.current.length > 0,
            excludeNodeIds: (mentionEditor) =>
              new Set(flowNodeMentionIdsFromText(mentionEditor.getText({ blockSeparator: "\n" }))),
          },
        ),
      }),
    ],
    // Placeholder fixed at mount; skills and node catalog via refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const editor = useEditor({
    extensions,
    content: composerDoc(value, [...imageMentionItems, ...nodeMentionItems]),
    editable: !disabled,
    immediatelyRender: false,
    coreExtensionOptions: {
      clipboardTextSerializer: { blockSeparator: "\n" },
    },
    editorProps: {
      attributes: {
        class: cn(
          "agent-composer-prosemirror it2tv-scrollbar w-full px-3 pb-2 text-xs text-ui-text-primary outline-none",
          edgeClearance ? "pt-5" : "pt-2",
        ),
      },
      handleKeyDown: (view, event) => {
        if (event.key === "Enter") {
          // Slash menu and @-mentions own Enter/Tab while open.
          if (isSkillSlashSuggestionActive() || isMentionSuggestionActive()) return false
          event.preventDefault()
          if (event.shiftKey) {
            // Перевод строки — новым параграфом: getText склеивает их через \n, а
            // composerDoc так же и разбирает обратно. HardBreak тут не подходит —
            // getText его не сериализует, и строка терялась бы на round-trip.
            // Базовый keymap Shift+Enter не перехватывает, поэтому делаем это сами.
            splitBlock(view.state, view.dispatch)
            return true
          }
          onSubmitRef.current()
          return true
        }
        return false
      },
      handleDOMEvents: {
        focus: () => {
          onFocusRef.current?.()
          return false
        },
      },
    },
    onUpdate: ({ editor: ed }) => {
      const text = editorPlainText(ed)
      lastEmittedRef.current = text
      onChangeRef.current(text)
    },
  })

  useEffect(() => {
    if (!editor) return
    editor.setEditable(!disabled)
  }, [disabled, editor])

  useEffect(() => {
    const dom = editor?.view.dom
    if (!dom) return
    dom.classList.toggle("pt-5", edgeClearance)
    dom.classList.toggle("pt-2", !edgeClearance)
  }, [editor, edgeClearance])

  useEffect(() => {
    if (!editor) return
    if (value === editorPlainText(editor)) {
      lastEmittedRef.current = value
      return
    }
    lastEmittedRef.current = value
    editor.commands.setContent(composerDoc(value, mentionItemsRef.current), { emitUpdate: false })
  }, [value, editor])

  useEffect(() => {
    if (!editor) return
    const items = nodeMentionItemsRef.current
    let tr = editor.state.tr
    let changed = false
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name !== "mention" || node.attrs.category !== "node") return
      const item = items.find((entry) => entry.category === "node" && entry.id === Number(node.attrs.id))
      if (!item || item.label === node.attrs.label) return
      tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, label: item.label })
      changed = true
    })
    if (!changed) return
    editor.view.dispatch(tr)
  }, [editor, nodeMentionItems])

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        editor?.commands.focus("end")
      },
      getText: () => editorPlainText(editor),
      clear: () => {
        if (!editor) return
        lastEmittedRef.current = ""
        editor.commands.clearContent(true)
        onChangeRef.current("")
      },
      insertSkill: (slug: string) => {
        if (!editor) return
        const current = editorPlainText(editor)
        if (hasSkillSlashToken(current, slug)) {
          editor.commands.focus("end")
          return
        }
        const token = `/${slug} `
        const needsLeadingSpace = current.length > 0 && !/\s$/.test(current)
        const insert = needsLeadingSpace ? ` ${token}` : token
        editor.chain().focus("end").insertContent(insert).run()
        const next = editorPlainText(editor)
        lastEmittedRef.current = next
        onChangeRef.current(next)
      },
      insertText: (text: string) => {
        if (!editor) return
        insertDictatedText(editor, text)
        const next = editorPlainText(editor)
        lastEmittedRef.current = next
        onChangeRef.current(next)
      },
    }),
    [editor],
  )

  const handleChromeMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (!editor || disabled) return
    const target = event.target
    if (!(target instanceof Element)) return
    if (target.closest("button, a, input, [role='listbox'], [role='menu']")) return

    // Пустое поле: caretRangeFromPoint попадает только в плейсхолдер/`<br>`, а клик
    // правее или ниже — в обёртку. Тогда posAtCoords null, и курсор не ставится.
    if (editor.view.posAtCoords({ left: event.clientX, top: event.clientY })) return

    event.preventDefault()
    editor.commands.focus("end")
  }

  return (
    <EditorContent
      editor={editor}
      onMouseDown={handleChromeMouseDown}
      className={cn(
        "w-full cursor-text [&_.ProseMirror]:cursor-text [&_.ProseMirror]:outline-none",
        expanded
          ? "[&_.ProseMirror]:max-h-40 [&_.ProseMirror]:min-h-[4.5rem] [&_.ProseMirror]:overflow-y-auto"
          : "[&_.ProseMirror]:max-h-10 [&_.ProseMirror]:min-h-10 [&_.ProseMirror]:overflow-hidden",
        className,
        disabled && "pointer-events-none opacity-50",
      )}
    />
  )
})
